import { create, type StoreApi } from 'zustand';
import toast from 'react-hot-toast';
import { BusinessSegment, ClientOption, ComplexityLevel, FeedItemAssignmentDto, FeedItemDto, GetFeedItemsQuery, ImpactLevel, ProcessRssFeedsResult, ProductType, ProductTypeInfo, ReleaseTypeDto, ReviewStatus, TagOption, UserLookupDto } from '@/shared/types/api';
import { ApiClient, api, getErrorMessage, getErrorStatus } from '@/shared/services/api';
import { releaseTypeApi } from '@/shared/services/releaseTypeApi';
import { userLookupApi } from '@/shared/services/userLookupApi';

interface FeedState {
  items: FeedItemDto[];
  totalCount: number;
  currentPage: number;
  pageSize: number;
  isLoading: boolean;
  isClassifying: boolean;
  error: string | null;
  filters: GetFeedItemsQuery;
  productTypes: ProductTypeInfo[];
  searchTerm: string;
  selectedProductType: ProductType | '';
  showUnreadOnly: boolean;
  showStarredOnly: boolean;
  selectedSegments: BusinessSegment[];
  // SC-242 filter state. Dates are ISO yyyy-MM-dd as produced by <input type="date">.
  selectedTags: string[];
  selectedClientIds: number[];
  dateFrom: string;
  dateTo: string;
  /** The active tags, in the admin-set display order (SC-376). */
  availableTags: string[];
  /** Colour, description (SC-272 guidance) and order of each active tag, keyed by name (SC-376). */
  tagMeta: Record<string, TagOption>;
  /**
   * True once the active vocabulary has loaded. Only then can a tag missing from it be shown as
   * a legacy (removed) tag rather than just "not loaded yet".
   */
  tagsLoaded: boolean;
  clients: ClientOption[];
  // SC-234: set by the dashboard's Critical / High Impact cards so their counts are
  // reproducible in the feed list.
  selectedComplexity: ComplexityLevel | '';
  selectedImpact: ImpactLevel | '';
  // SC-300/SC-301: Release Type filter + the active vocabulary backing it.
  selectedReleaseType: string;
  availableReleaseTypes: string[];
  // SC-278/SC-301: version/deletion filters.
  showMultiVersion: boolean;
  showDeleted: boolean;
  // SC-423: Assigned to User (any of) + Review by Date filters, and every user - inactive
  // included, so stale assignments can be found - to pick from.
  selectedAssigneeIds: string[];
  selectedReviewStatus: ReviewStatus | '';
  users: UserLookupDto[];
}

/** Retained UI-filter state; every field is optional so callers can patch one at a time. */
export interface SearchFilterPatch {
  searchTerm?: string;
  selectedProductType?: ProductType | '';
  showUnreadOnly?: boolean;
  showStarredOnly?: boolean;
  selectedSegments?: BusinessSegment[];
  selectedTags?: string[];
  selectedClientIds?: number[];
  dateFrom?: string;
  dateTo?: string;
  selectedComplexity?: ComplexityLevel | '';
  selectedImpact?: ImpactLevel | '';
  selectedReleaseType?: string;
  showMultiVersion?: boolean;
  showDeleted?: boolean;
  selectedAssigneeIds?: string[];
  selectedReviewStatus?: ReviewStatus | '';
}

interface FeedActions {
  loadFeedItems: (query?: GetFeedItemsQuery) => Promise<void>;
  markItemAsRead: (itemId: number, userId: string, isRead: boolean) => Promise<boolean>;
  markItemAsStarred: (itemId: number, userId: string, isStarred: boolean) => Promise<boolean>;
  updateSegments: (itemId: number, segments: BusinessSegment[]) => Promise<boolean>;
  updateTags: (itemId: number, tags: string[]) => Promise<boolean>;
  updateReleaseType: (itemId: number, releaseType: string | null) => Promise<boolean>;
  updateItemDeleted: (itemId: number, isDeleted: boolean) => Promise<boolean>;
  /** SC-423: reflect a saved (or cleared, with null) assignment on the loaded row; re-queries the
   * page when the list is filtered on assignment. */
  setItemAssignment: (itemId: number, assignment: FeedItemAssignmentDto | null) => void;
  setFilters: (filters: Partial<GetFeedItemsQuery>) => void;
  setSearchFilters: (filters: SearchFilterPatch) => void;
  clearFilters: () => void;
  loadProductTypes: () => Promise<void>;
  loadFilterOptions: () => Promise<void>;
  /** (Re)loads the active tag vocabulary from /classification/options (SC-376). */
  loadTagOptions: () => Promise<void>;
  /** Loads the tag vocabulary unless it is loaded or already loading. */
  ensureTagOptions: () => Promise<void>;
  /** Marks the vocabulary stale (after an admin edit) so the next chip or picker reloads it. */
  invalidateTagOptions: () => void;
  processRssFeeds: () => Promise<ProcessRssFeedsResult | null>;
  setError: (error: string | null) => void;
  clearError: () => void;
}

type FeedStore = FeedState & FeedActions;

export const defaultFilters: GetFeedItemsQuery = {
  page: 1,
  pageSize: 20,
};

// SC-376: one in-flight vocabulary request shared by every chip that asks for it.
let tagOptionsRequest: Promise<void> | null = null;
// Bumped by invalidateTagOptions: a vocabulary response from a request started before the latest
// invalidation is stale (it may predate an admin's rename or removal) and must not be applied.
let tagOptionsGeneration = 0;

const POLL_INTERVAL_MS = 5000;
const NO_PROGRESS_TIMEOUT_MS = 90_000;
const ABSOLUTE_CEILING_MS = 1_200_000;

// SC-295: real-time filtering fires many overlapping loads. This generation counter lets a
// slower earlier request drop its result instead of overwriting a newer one (stale-page guard).
let loadGeneration = 0;

let pollTimer: ReturnType<typeof setInterval> | null = null;
let pendingIds = new Set<number>();
let lastProgressAt = 0;
let pollAbsoluteDeadline = 0;
let pollGeneration = 0;

type SetFeedStore = StoreApi<FeedStore>['setState'];
type GetFeedStore = StoreApi<FeedStore>['getState'];

const refreshFeedItemsSilently = async (set: SetFeedStore, get: GetFeedStore) => {
  // Participate in the same generation guard as loadFeedItems, so a 5s poll refresh started with
  // old filters can't land after (and clobber) a newer user-initiated filtered load (review L4).
  const generation = ++loadGeneration;
  try {
    const result = await ApiClient.getFeedItems(get().filters);
    if (generation !== loadGeneration) return;

    set({
      items: result.items,
      totalCount: result.totalCount,
      currentPage: result.page,
      pageSize: result.pageSize,
    });
  } catch (error) {
    console.error('Failed to refresh feed items:', error);
  }
};

const stopClassificationPolling = (set: SetFeedStore) => {
  if (pollTimer) {
    clearInterval(pollTimer);
    pollTimer = null;
  }
  pendingIds = new Set<number>();
  set({ isClassifying: false });
};

const startClassificationPolling = (ids: number[], set: SetFeedStore, get: GetFeedStore) => {
  if (pollTimer) {
    clearInterval(pollTimer);
    pollTimer = null;
  }

  pendingIds = new Set<number>([...pendingIds, ...ids]);
  set({ isClassifying: true });

  const now = Date.now();
  lastProgressAt = now;
  pollAbsoluteDeadline = now + ABSOLUTE_CEILING_MS;
  pollGeneration += 1;
  const cycleGeneration = pollGeneration;

  pollTimer = setInterval(() => {
    void (async () => {
      const prevSize = pendingIds.size;

      try {
        const checkedIds = [...pendingIds];
        const { statuses } = await ApiClient.getClassificationStatus(checkedIds);

        if (cycleGeneration !== pollGeneration) {
          return;
        }

        const statusById = new Map(statuses.map((status) => [status.id, status.isClassified]));

        checkedIds.forEach((id) => {
          const isClassified = statusById.get(id);
          if (isClassified === true || isClassified === undefined) {
            pendingIds.delete(id);
          }
        });

        if (pendingIds.size < prevSize) {
          lastProgressAt = Date.now();
        }

        await refreshFeedItemsSilently(set, get);
      } catch (error) {
        console.error('Failed to poll classification status:', error);
      }

      if (cycleGeneration !== pollGeneration) {
        return;
      }

      if (pendingIds.size === 0) {
        stopClassificationPolling(set);
        return;
      }

      const now = Date.now();
      if (now > pollAbsoluteDeadline || now - lastProgressAt > NO_PROGRESS_TIMEOUT_MS) {
        stopClassificationPolling(set);
        toast('Some items are still classifying — refresh in a moment', { icon: '⏳' });
      }
    })();
  }, POLL_INTERVAL_MS);
};

export const useFeedStore = create<FeedStore>((set, get) => ({
  // Initial state
  items: [],
  totalCount: 0,
  currentPage: 1,
  pageSize: 20,
  isLoading: false,
  isClassifying: false,
  error: null,
  filters: defaultFilters,
  productTypes: [],

  searchTerm: '',
  selectedProductType: '',
  showUnreadOnly: false,
  showStarredOnly: false,
  selectedSegments: [],
  selectedTags: [],
  selectedClientIds: [],
  dateFrom: '',
  dateTo: '',
  availableTags: [],
  tagMeta: {},
  tagsLoaded: false,
  clients: [],
  selectedComplexity: '',
  selectedImpact: '',
  selectedReleaseType: '',
  availableReleaseTypes: [],
  showMultiVersion: false,
  showDeleted: false,
  selectedAssigneeIds: [],
  selectedReviewStatus: '',
  users: [],

  // Actions
  loadFeedItems: async (query?: GetFeedItemsQuery) => {
    const generation = ++loadGeneration;
    set({ isLoading: true, error: null });

    try {
      const currentFilters = query || get().filters;
      const result = await ApiClient.getFeedItems(currentFilters);

      // A newer load started while this one was in flight — drop this (stale) result so the
      // list can't flash back to an older page/filter.
      if (generation !== loadGeneration) return;

      set({
        items: result.items,
        totalCount: result.totalCount,
        currentPage: result.page,
        pageSize: result.pageSize,
        isLoading: false,
        filters: currentFilters,
      });
    } catch (error) {
      if (generation !== loadGeneration) return;
      const errorMessage = getErrorMessage(error, 'Failed to load feed items');
      set({
        isLoading: false,
        error: errorMessage,
      });
    }
  },

  markItemAsRead: async (itemId: number, userId: string, isRead: boolean) => {
    try {
      const result = await ApiClient.markItemAsRead(itemId, { userId, isRead });

      if (result.success) {
        // Update the local state
        set((state) => ({
          items: state.items.map((item) =>
            item.id === itemId
              ? {
                  ...item,
                  isRead,
                  readAt: isRead ? new Date().toISOString() : undefined,
                }
              : item
          ),
        }));
        return true;
      } else {
        set({ error: result.message });
        return false;
      }
    } catch (error) {
      const errorMessage = getErrorMessage(error, 'Failed to update read status');
      set({ error: errorMessage });
      return false;
    }
  },

  markItemAsStarred: async (itemId: number, userId: string, isStarred: boolean) => {
    try {
      const result = await ApiClient.markItemAsStarred(itemId, { userId, isStarred });

      if (result.success) {
        // Update the local state
        set((state) => ({
          items: state.items.map((item) =>
            item.id === itemId
              ? {
                  ...item,
                  isStarred,
                  starredAt: isStarred ? new Date().toISOString() : undefined,
                }
              : item
          ),
        }));
        return true;
      } else {
        set({ error: result.message });
        return false;
      }
    } catch (error) {
      const errorMessage = getErrorMessage(error, 'Failed to update starred status');
      set({ error: errorMessage });
      return false;
    }
  },

  updateSegments: async (itemId: number, segments: BusinessSegment[]) => {
    try {
      const result = await ApiClient.updateSegments(itemId, { segments });

      if (result.success) {
        set((state) => ({
          items: state.items.map((item) =>
            item.id === itemId
              ? {
                  ...item,
                  businessSegments: result.segments ?? segments,
                  businessSegmentsSource: result.source ?? 'Manual',
                }
              : item
          ),
        }));
        return true;
      } else {
        set({ error: result.message });
        return false;
      }
    } catch (error) {
      const errorMessage = getErrorMessage(error, 'Failed to update business segments');
      set({ error: errorMessage });
      return false;
    }
  },

  // SC-300: assign (or clear, with null) the Release Type value on an item. The value is
  // validated server-side against the active vocabulary.
  updateReleaseType: async (itemId: number, releaseType: string | null) => {
    try {
      await releaseTypeApi.assign(itemId, releaseType);
      set((state) => ({
        items: state.items.map((item) =>
          item.id === itemId ? { ...item, releaseType: releaseType ?? null } : item
        ),
      }));
      return true;
    } catch (error) {
      set({ error: getErrorMessage(error, 'Failed to update release type') });
      return false;
    }
  },

  // SC-278: admin marks an item deleted / restores it. Updates in place so the DELETED badge
  // reflects immediately; a filter change re-queries and drops/keeps it as appropriate.
  updateItemDeleted: async (itemId: number, isDeleted: boolean) => {
    try {
      await ApiClient.setItemDeleted(itemId, isDeleted);
      set((state) => ({
        items: state.items.map((item) => (item.id === itemId ? { ...item, isDeleted } : item)),
      }));
      return true;
    } catch (error) {
      set({ error: getErrorMessage(error, 'Failed to update deleted status') });
      return false;
    }
  },

  // SC-423: the modal has already saved; this updates the row in place so the icon state follows
  // at once. When the list is filtered on assignment, the change can move the row in or out of
  // the filter (reassigned, date moved, cleared), so the current page is also re-queried quietly
  // to keep the rows and the total in step with the filter chips.
  setItemAssignment: (itemId: number, assignment: FeedItemAssignmentDto | null) => {
    set((state) => ({
      items: state.items.map((item) => (item.id === itemId ? { ...item, assignment } : item)),
    }));
    const { assignedUserId, reviewStatus } = get().filters;
    if ((assignedUserId && assignedUserId.length > 0) || reviewStatus) {
      void refreshFeedItemsSilently(set, get);
    }
  },

  // SC-240. Admin-only server-side; a non-admin gets a 403 which surfaces as an error toast.
  updateTags: async (itemId: number, tags: string[]) => {
    try {
      await ApiClient.updateTags(itemId, tags);

      set((state) => ({
        items: state.items.map((item) => (item.id === itemId ? { ...item, tags } : item)),
      }));
      return true;
    } catch (error) {
      set({ error: getErrorMessage(error, 'Failed to update tags') });
      return false;
    }
  },

  setSearchFilters: (searchFilters: SearchFilterPatch) => {
    const current = get();
    set({
      searchTerm: searchFilters.searchTerm ?? current.searchTerm,
      selectedProductType: searchFilters.selectedProductType ?? current.selectedProductType,
      showUnreadOnly: searchFilters.showUnreadOnly ?? current.showUnreadOnly,
      showStarredOnly: searchFilters.showStarredOnly ?? current.showStarredOnly,
      selectedSegments: searchFilters.selectedSegments ?? current.selectedSegments,
      selectedTags: searchFilters.selectedTags ?? current.selectedTags,
      selectedClientIds: searchFilters.selectedClientIds ?? current.selectedClientIds,
      dateFrom: searchFilters.dateFrom ?? current.dateFrom,
      dateTo: searchFilters.dateTo ?? current.dateTo,
      selectedComplexity: searchFilters.selectedComplexity ?? current.selectedComplexity,
      selectedImpact: searchFilters.selectedImpact ?? current.selectedImpact,
      selectedReleaseType: searchFilters.selectedReleaseType ?? current.selectedReleaseType,
      showMultiVersion: searchFilters.showMultiVersion ?? current.showMultiVersion,
      showDeleted: searchFilters.showDeleted ?? current.showDeleted,
      selectedAssigneeIds: searchFilters.selectedAssigneeIds ?? current.selectedAssigneeIds,
      selectedReviewStatus: searchFilters.selectedReviewStatus ?? current.selectedReviewStatus,
    });
  },

  setFilters: (newFilters: Partial<GetFeedItemsQuery>) => {
    const currentFilters = get().filters;
    const updatedFilters = { ...currentFilters, ...newFilters };

    set({ filters: updatedFilters });

    // Auto-reload with new filters
    get().loadFeedItems(updatedFilters);
  },

  clearFilters: () => {
    set({ filters: defaultFilters });
    get().loadFeedItems(defaultFilters);
  },

  loadProductTypes: async () => {
    try {
      const productTypes = await ApiClient.getProductTypes();
      set({ productTypes });
    } catch (error) {
      console.error('Failed to load product types:', error);
      // Set default product types if API fails
      set({
        productTypes: [
          { value: ProductType.General, name: 'General', description: 'General notices and announcements', displayName: 'Notice Of Change' },
          { value: ProductType.CorePlatform, name: 'CorePlatform', description: 'Core platform release notes', displayName: 'Core Release Note' },
          { value: ProductType.ApiChangelog, name: 'ApiChangelog', description: 'API changes and updates', displayName: 'API Changelog' },
          { value: ProductType.Portal, name: 'Portal', description: 'Portal interface updates', displayName: 'Portal' },
          { value: ProductType.SalesforceApp, name: 'SalesforceApp', description: 'Salesforce integration updates', displayName: 'Salesforce' },
          { value: ProductType.TransactionMonitoring, name: 'TransactionMonitoring', description: 'Transaction monitoring updates', displayName: 'Transaction Monitoring' },
        ],
      });
    }
  },

  // SC-376: the admin-managed active tags, in display order, with colour and description. A
  // failure leaves the previous vocabulary in place - chips fall back to the neutral style.
  loadTagOptions: async () => {
    const generation = tagOptionsGeneration;
    const request = (async () => {
      try {
        const { data } = await api.get<{ commonTags?: string[]; tagDescriptions?: TagOption[] }>(
          '/classification/options'
        );
        if (generation !== tagOptionsGeneration) return;
        const options = [...(data.tagDescriptions ?? [])].sort(
          (a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0)
        );
        set({
          availableTags: options.length > 0 ? options.map((t) => t.name) : data.commonTags ?? [],
          tagMeta: Object.fromEntries(options.map((t) => [t.name, t])),
          tagsLoaded: true,
        });
      } catch (error) {
        console.error('Failed to load tag options:', error);
      }
    })();
    tagOptionsRequest = request;
    try {
      await request;
    } finally {
      if (tagOptionsRequest === request) tagOptionsRequest = null;
    }
  },

  ensureTagOptions: async () => {
    if (get().tagsLoaded) return;
    if (tagOptionsRequest) return tagOptionsRequest;
    return get().loadTagOptions();
  },

  invalidateTagOptions: () => {
    tagOptionsGeneration += 1;
    // A request already in flight carries the old vocabulary: let the next ensureTagOptions start
    // a fresh one rather than join it.
    tagOptionsRequest = null;
    set({ tagsLoaded: false });
  },

  // SC-242: the tag and client vocabularies backing the new multi-select filters. A failure
  // leaves the lists empty so the rest of the feed still works - the filters just have
  // nothing to offer rather than the page erroring.
  loadFilterOptions: async () => {
    const [, clientsResult, releaseTypesResult, usersResult] = await Promise.allSettled([
      get().loadTagOptions(),
      api.get<ClientOption[]>('/clients'),
      releaseTypeApi.list(),
      // SC-423: all users regardless of status, for the Assigned to User filter.
      userLookupApi.list(false),
    ]);

    if (clientsResult.status === 'fulfilled') {
      set({
        clients: clientsResult.value.data.map((c) => ({ id: c.id, name: c.name })),
      });
    } else {
      console.error('Failed to load client filter options:', clientsResult.reason);
    }

    // SC-300/SC-301: active Release Type names for the feed filter.
    if (releaseTypesResult.status === 'fulfilled') {
      set({ availableReleaseTypes: releaseTypesResult.value.map((r: ReleaseTypeDto) => r.name) });
    } else {
      console.error('Failed to load release type filter options:', releaseTypesResult.reason);
    }

    if (usersResult.status === 'fulfilled') {
      set({ users: usersResult.value });
    } else {
      console.error('Failed to load user filter options:', usersResult.reason);
    }
  },

  processRssFeeds: async () => {
    set({ isLoading: true, error: null });

    try {
      const result = await ApiClient.processRssFeeds();

      await refreshFeedItemsSilently(set, get);

      set({ isLoading: false });

      if (result.newItemIds.length > 0) {
        startClassificationPolling(result.newItemIds, set, get);
      }

      return result;
    } catch (error) {
      const status = getErrorStatus(error);
      const errorMessage =
        status === 403
          ? 'You need admin privileges to process feeds'
          : getErrorMessage(error, 'Failed to process RSS feeds');
      set({
        isLoading: false,
        error: errorMessage,
      });
      return null;
    }
  },

  setError: (error: string | null) => set({ error }),
  clearError: () => set({ error: null }),
}));